import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { variables } from "../../config/Variables";
import { toast } from "react-toastify";
import stepIcon from "./stepIcon.png"
import pdfIcon from "./pdfIcon.png"
import ucdIcon from "./3dIcon.png"
import schemaIcon from "./schemaIcon.png"
import { useTranslation } from "react-i18next";
import IFrameWindow from "../structure/IFrameWindow";
import productService from "../../services/productService";
import productCache from "../../services/productCache";
import { formatKatalogKodu } from "../../utils/formatters";
import { IconSearch, IconChevronRight, IconFiles, IconPhoto, IconFileDescription, Icon3dCubeSphere, IconLayoutGrid, IconInfoCircle, IconTag, IconAlertTriangle } from "@tabler/icons-react";
import { KamaModal } from "../structure/KamaModal";
import { QuickSearchInput } from "../structure/QuickSearchInput";

export const ProductList = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();

    // State Tanımlamaları
    const [productGroups, setProductGroups] = useState([]);
    const [groupCode, setGroupCode] = useState('*');
    const [rootGroupCode, setRootGroupCode] = useState('');
    const [sortOrder, setSortOrder] = useState("ASC");
    const [products, setProducts] = useState([]);
    const [aranacakMetin, setAranacakMetin] = useState('');
    const [model3bCheck, setModel3bCheck] = useState('0');
    const [detailList, setDetailList] = useState("no");
    
    // UI State'leri
    const [downloadingId, setDownloadingId] = useState(null); // Format: "productId-fileType" (e.g., "10-pdf")
    const [productUrl, setProductUrl] = useState("");
    const [imgSchema, setImgSchema] = useState("");
    const [imgProduct, setImgProduct] = useState("");
    const [orjinalKodu, setOrjinalKodu] = useState("");
    const [showSchemaModal, setShowSchemaModal] = useState(false);
    const [showImgModal, setShowImgModal] = useState(false);
    const [show3dModal, setShow3dModal] = useState(false);
    const [showSpecModal, setShowSpecModal] = useState(false);
    const [parsedSpecData, setParsedSpecData] = useState(null);
    const [specLoading, setSpecLoading] = useState(false);

    // Sabitler
    const ImagePath = variables.IMAGE_URL;
    const currentLanguage = i18n.language; // Hook'tan gelen dil bilgisi

    // Lifecycle: ComponentDidMount
    useEffect(() => {
        const savedState = location.state;
        if (savedState) {
            if (savedState.groupCode) setGroupCode(savedState.groupCode);
            if (savedState.rootGroupCode) setRootGroupCode(savedState.rootGroupCode);
            if (savedState.aranacakMetin) setAranacakMetin(savedState.aranacakMetin);
            if (savedState.model3bCheck) setModel3bCheck(savedState.model3bCheck);
            if (savedState.detailList) setDetailList(savedState.detailList);
            
            if (savedState.detailList === 'yes') {
                refreshList(savedState.aranacakMetin, savedState.groupCode);
            } else {
                refreshGroups(savedState.groupCode);
            }
        } else {
            refreshGroups();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const sorting = (columnName) => {
        let sortedList = [...products];
        if (sortOrder === "ASC") {
            sortedList.sort((a, b) => a[columnName] > b[columnName] ? 1 : -1);
            setSortOrder("DSC");
        } else {
            sortedList.sort((a, b) => a[columnName] < b[columnName] ? 1 : -1);
            setSortOrder("ASC");
        }
        setProducts(sortedList);
    };

    const refreshList = async (searchText = aranacakMetin, targetGroupCode = groupCode) => {
        setDetailList('yes');
        const query = (searchText && searchText.trim() !== '' && searchText !== '*') ? searchText : targetGroupCode;
        if (query && query.trim() !== '' && query !== '*') {
            try {
                // Perform 0ms instant local filtering from cache
                const data = await productCache.filterLocalAll(query, model3bCheck === '1');
                if (data.length === 0) {
                    toast(t('mesajUrunBulunamadi'));
                }
                setProducts(data);
            } catch (error) {
                // Fallback to API if cache is unavailable
                try {
                    const apiData = await productService.getProducts(query, model3bCheck);
                    setProducts(apiData);
                } catch (err) {
                    toast.warning(t('mesajOturumZamanAsimi'));
                    navigate("/login");
                }
            }
        }
    };

    const refreshGroups = async (code = groupCode) => {
        setDetailList('no');
        try {
            const data = await productService.getProductGroups(code);
            
            if (data.length === 0) {
                toast(t('mesajUrunBulunamadi'));
            }
            setProductGroups(data);
        } catch (error) {
            toast.warning(t('mesajOturumZamanAsimi'));
            navigate("/login");
        }
    };

    const downloadPdfFile = async (product, index) => {
        const downloadKey = `${product.Id || index}-pdf`;
        setDownloadingId(downloadKey);
        toast.info(t('mesajPdfIndirmeBasladi'));
        
        try {
            const data = await productService.downloadFile(product.PdfFile, sessionStorage.getItem("activationKey"), product.Id, 'pdf');
            const url = window.URL.createObjectURL(new Blob([data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', product.PdfFile);
            document.body.appendChild(link);
            link.click();
            link.remove(); // Temizlik
        } catch (error) {
            console.error("PDF download failed", error);
            toast.error(t('mesajDosyaIndirmeHatasi'));
        } finally {
            setDownloadingId(null);
        }
    };

    const downloadStepFile = async (product, index) => {
        const downloadKey = `${product.Id || index}-step`;
        setDownloadingId(downloadKey);
        toast.info(t('mesajStepIndirmeBasladi'));

        try {
            const data = await productService.downloadFile(product.StepFile, sessionStorage.getItem("activationKey"), product.Id, 'step');
            const url = window.URL.createObjectURL(new Blob([data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', product.StepFile);
            document.body.appendChild(link);
            link.click();
            link.remove(); // Temizlik
        } catch (error) {
            console.error("Step file download failed", error);
            toast.error(t('mesajDosyaIndirmeHatasi'));
        } finally {
            setDownloadingId(null);
        }
    };

    const handleChangeAranacakMetin = (e) => {
        const val = e.target.value;
        setAranacakMetin(val);

        if (val.trim() === '') {
            setGroupCode('*');
            setRootGroupCode('');
            refreshGroups('*');
        }
    };

    const handleChangeModel3b = (e) => {
        const isChecked = e.target.checked;
        setModel3bCheck(isChecked ? "1" : "0");
    };

    const handleChangeGroupCode = (e) => {
        setGroupCode(e);
        
        if (e === "*" || e === "") {
            setAranacakMetin('');
        }

        if (e.split("-").length === 2) {
            setAranacakMetin('');
            refreshList(e);
            
            const rootCode = e.split("-");
            setRootGroupCode(rootCode[0]);
        } else {
            setRootGroupCode('');
            refreshGroups(e);
        }
    };

    const handleSearchClick = () => {
        setRootGroupCode('');
        refreshList();
    };

    const handleShowPopUpSchema = (urunObj) => {
        setImgSchema(variables.SCHEMA_URL + urunObj.SchemaFile);
        setOrjinalKodu(urunObj.OrjinalKodu);
        setShowSchemaModal(true);
    };

    const handleShowPopUpImg = (urunObj) => {
        setImgProduct(variables.IMAGE_URL + urunObj.ImageFile);
        setOrjinalKodu(urunObj.OrjinalKodu);
        setShowImgModal(true);
    };

    const handleShowPopUp3d = (pUrl, pCode) => {
        setProductUrl(pUrl);
        setOrjinalKodu(pCode);
        setShow3dModal(true);
    };

    const handleShowSpecModal = async (katalogKodu) => {
        if (!katalogKodu) return;
        setSpecLoading(true);
        try {
            const firstToken = katalogKodu.split(/[\s\xa0]+/)[0];
            const cleanCode = firstToken.replace(/\s+/g, "");
            const data = await productService.parseCode(cleanCode);
            if (data) {
                setParsedSpecData(data);
                setShowSpecModal(true);
            } else {
                toast.error(t('mesajKodCozumlenemedi', 'Kod çözümlenemedi.'));
            }
        } catch (error) {
            console.error("Error parsing code", error);
            toast.error(t('mesajKodBulunamadi', 'Belirttiğiniz Teker Kodu Kama Rulet Kodlama Sistemine uygun olmadığı için çözülememiştir.'));
        } finally {
            setSpecLoading(false);
        }
    };

    const thStyle = {
        fontSize: '11px',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        color: '#94a3b8',
        padding: '12px 16px',
        borderBottom: '2px solid #f1f5f9',
        whiteSpace: 'nowrap',
        background: '#f8fafc',
    };

    const actionBtn = (bg, color) => ({
        background: bg,
        border: 'none',
        borderRadius: '8px',
        padding: '6px 8px',
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        lineHeight: 1,
        color,
    });

    const groupsByCategory = productGroups.reduce((acc, prdGrp) => {
        const catName = i18n.language === 'tr' ? prdGrp.KategoriAdi : prdGrp.Category;
        if (!acc[catName]) acc[catName] = [];
        acc[catName].push(prdGrp);
        return acc;
    }, {});

    return (
        <div style={{ maxWidth: '1200px' }}>

            {/* ── Toolbar ── */}
            <div className="card border-0 shadow-sm rounded-4 mb-4" style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                <div className="card-body p-3 d-flex align-items-center justify-content-between gap-3 flex-wrap">

                    {/* Breadcrumb */}
                    <div className="d-flex align-items-center gap-1 flex-wrap" style={{ fontSize: '14px' }}>
                        <span
                            onClick={() => handleChangeGroupCode('*')}
                            style={{ cursor: 'pointer', color: '#2563a8', fontWeight: 600 }}
                        >
                            {t('anaUrunGruplari')}
                        </span>
                        {groupCode !== '*' && groupCode.split("-").length === 1 && (
                            <>
                                <IconChevronRight size={14} color="#94a3b8" />
                                <span style={{ color: '#475569' }}>{groupCode}</span>
                            </>
                        )}
                        {rootGroupCode !== "" && (
                            <>
                                <IconChevronRight size={14} color="#94a3b8" />
                                <span
                                    onClick={() => handleChangeGroupCode(rootGroupCode)}
                                    style={{ cursor: 'pointer', color: '#2563a8', fontWeight: 600 }}
                                >
                                    {rootGroupCode}
                                </span>
                                <IconChevronRight size={14} color="#94a3b8" />
                                <span style={{ color: '#475569' }}>{groupCode}</span>
                            </>
                        )}
                    </div>

                    {/* Arama */}
                    <div className="d-flex align-items-center gap-3">
                        <QuickSearchInput
                            value={aranacakMetin}
                            onChange={handleChangeAranacakMetin}
                            onSearchSubmit={(val) => {
                                const searchVal = val !== undefined ? val : aranacakMetin;
                                if (searchVal && searchVal.trim() !== '') {
                                    setRootGroupCode('');
                                    refreshList(searchVal);
                                } else {
                                    setGroupCode('*');
                                    setRootGroupCode('');
                                    refreshGroups('*');
                                }
                            }}
                            placeholder={t('aranacakMetin')}
                            width="480px"
                        />
                    </div>
                </div>
            </div>

            {/* ── Warning Bar ── */}
            <div className="card border-0 shadow-sm rounded-4 mb-4" style={{
                background: 'linear-gradient(135deg, #fffbe6 0%, #fff7ed 100%)',
                border: '1px solid #fde68a',
                borderLeft: '4px solid #f59e0b'
            }}>
                <div className="card-body p-3 d-flex align-items-center gap-3 flex-wrap">
                    <div className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0" style={{ width: '38px', height: '38px', background: '#fef3c7', color: '#d97706' }}>
                        <IconAlertTriangle size={20} />
                    </div>
                    <div className="flex-grow-1" style={{ color: '#92400e', fontSize: '14px', fontWeight: 500, lineHeight: 1.5 }}>
                        {t('uyariTeknikCizimMail', 'Bulamadığınız Teknik Çizimler için')}{' '}
                        <a href="mailto:satis@kama.com.tr" className="fw-bold text-decoration-none" style={{ color: '#b45309', borderBottom: '1px dashed #b45309' }}>
                            satis@kama.com.tr
                        </a>{' '}
                        veya{' '}
                        <a href="mailto:arge@kama.com.tr" className="fw-bold text-decoration-none" style={{ color: '#b45309', borderBottom: '1px dashed #b45309' }}>
                            arge@kama.com.tr
                        </a>{' '}
                        ye mail atabilirsiniz.
                    </div>
                </div>
            </div>

            {/* ── Grup Görünümü ── */}
            {detailList === 'no' && (
                <div>
                    {/* Kategori hızlı nav */}
                    {Object.keys(groupsByCategory).length > 1 && (
                        <div className="d-flex gap-2 mb-4 flex-wrap" style={{ position: 'sticky', top: 68, zIndex: 9, background: '#ebebeb', paddingTop: 8, paddingBottom: 8 }}>
                            {Object.keys(groupsByCategory).map(catName => (
                                <span
                                    key={catName}
                                    onClick={() => document.getElementById('cat-' + catName)?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                                    style={{
                                        background: '#eff6ff', color: '#2563a8', cursor: 'pointer',
                                        fontWeight: 600, fontSize: '13px', padding: '5px 14px',
                                        borderRadius: '20px', userSelect: 'none',
                                    }}
                                >
                                    {catName}
                                </span>
                            ))}
                        </div>
                    )}

                    {/* Kategori bölümleri */}
                    {Object.keys(groupsByCategory).map(categoryName => (
                        <div key={categoryName} id={'cat-' + categoryName} className="mb-5" style={{ scrollMarginTop: '80px' }}>
                            <div className="d-flex align-items-center justify-content-between mb-3">
                                <div style={{
                                    fontSize: '11px', fontWeight: 700, textTransform: 'uppercase',
                                    letterSpacing: '1px', color: '#94a3b8',
                                    display: 'flex', alignItems: 'center', gap: '8px',
                                }}>
                                    <IconFiles size={14} />
                                    {categoryName}
                                </div>
                                <span
                                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                                    style={{ cursor: 'pointer', color: '#94a3b8', fontSize: '12px' }}
                                >
                                    ↑ {t('anaUrunGruplari')}
                                </span>
                            </div>
                            <div style={{ height: '1px', background: '#e2e8f0', marginBottom: '20px' }} />

                            <div className="row g-3">
                                {groupsByCategory[categoryName].map(prdGrp => (
                                    <div className="col-6 col-sm-4 col-md-3 col-xl-2" key={prdGrp.GrupKodu}>
                                        <div
                                            className="card border-0 shadow-sm rounded-4 h-100 overflow-hidden"
                                            style={{ cursor: 'pointer', transition: 'transform 0.15s, box-shadow 0.15s', position: 'relative' }}
                                            onClick={() => handleChangeGroupCode(prdGrp.GrupKodu)}
                                            onMouseEnter={e => {
                                                e.currentTarget.style.transform = 'translateY(-4px)';
                                                e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.12)';
                                            }}
                                            onMouseLeave={e => {
                                                e.currentTarget.style.transform = 'translateY(0)';
                                                e.currentTarget.style.boxShadow = '';
                                            }}
                                        >
                                            {/* Ürün sayısı badge */}
                                            <div style={{
                                                position: 'absolute', top: 8, right: 8,
                                                background: '#2563a8', color: '#fff',
                                                borderRadius: '20px', fontSize: '11px',
                                                fontWeight: 700, padding: '3px 8px',
                                                lineHeight: 1, display: 'flex', alignItems: 'center',
                                            }}>
                                                {prdGrp.UrunSayisi}
                                            </div>

                                            {/* Görsel */}
                                            <div style={{
                                                height: 130, display: 'flex', alignItems: 'center',
                                                justifyContent: 'center', padding: '16px', background: '#fafafa',
                                            }}>
                                                <img
                                                    src={ImagePath + prdGrp.ImageFile}
                                                    alt={prdGrp.GrupKodu}
                                                    style={{ maxHeight: '100px', maxWidth: '100%', objectFit: 'contain' }}
                                                />
                                            </div>

                                            {/* Grup kodu */}
                                            <div style={{
                                                padding: '10px', borderTop: '1px solid #f1f5f9',
                                                textAlign: 'center',
                                            }}>
                                                <span style={{ fontWeight: 700, fontSize: '15px', color: '#1e293b' }}>
                                                    {prdGrp.GrupKodu}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* ── Ürün Tablosu ── */}
            {detailList === 'yes' && (
                <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
                    <table className="table table-hover mb-0 align-middle">
                        <thead>
                            <tr>
                                <th style={thStyle}>{t('tabloBaslikResim')}</th>
                                <th style={{ ...thStyle, cursor: 'pointer' }} onClick={() => sorting("OrjinalKodu")}>
                                    {t('tabloBaslikUrunKodu')} ↕
                                </th>
                                <th style={{ ...thStyle, cursor: 'pointer' }} onClick={() => sorting("KatalogKodu")}>
                                    {t('tabloBaslikAciklama')} ↕
                                </th>
                                <th style={{ ...thStyle, textAlign: 'center' }}>{t('tabloBaslikPdfOnizleme')}</th>
                                <th style={{ ...thStyle, textAlign: 'center' }}>{t('tabloBaslikPdfDosya')}</th>
                                <th style={{ ...thStyle, textAlign: 'center' }}>{t('tabloBaslikStepDosya')}</th>
                                <th style={{ ...thStyle, textAlign: 'center' }}>{t('tabloBaslikModelDosya')}</th>
                                <th style={{ ...thStyle, textAlign: 'center' }}>{t('tabloBaslikDetay', 'Detay')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {products.map((prd, index) => {
                                const pdfKey = `${prd.Id || index}-pdf`;
                                const stepKey = `${prd.Id || index}-step`;
                                return (
                                    <tr 
                                        key={prd.Id || index}
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => navigate(`/productdetails/${prd.ID || prd.Id}`, {
                                            state: {
                                                groupCode,
                                                rootGroupCode,
                                                aranacakMetin,
                                                model3bCheck,
                                                detailList
                                            }
                                        })}
                                    >
                                        <td style={{ padding: '10px 16px' }}>
                                            <img
                                                src={ImagePath + prd.ImageFile}
                                                width="50"
                                                alt=""
                                                style={{ borderRadius: '8px', cursor: 'pointer', objectFit: 'contain' }}
                                                onClick={(e) => { e.stopPropagation(); handleShowPopUpImg(prd); }}
                                            />
                                        </td>
                                        <td style={{ padding: '12px 16px', minWidth: '180px' }}>
                                            <div className="d-flex flex-column gap-1">
                                                <span style={{ fontWeight: 700, color: '#0f172a', fontSize: '14px' }}>
                                                    {prd.OrjinalKodu}
                                                </span>
                                                <div className="d-flex align-items-center gap-1.5">
                                                    <span style={{ color: '#64748b', fontSize: '12px', fontWeight: 500 }}>
                                                        {formatKatalogKodu(prd.KatalogKodu)}
                                                    </span>
                                                    <span 
                                                        role="button"
                                                        className="text-primary d-flex align-items-center justify-content-center"
                                                        style={{ cursor: "pointer", transition: "color 0.15s" }}
                                                        onClick={(e) => { 
                                                            e.stopPropagation(); 
                                                            handleShowSpecModal(prd.KatalogKodu); 
                                                        }}
                                                        title={t("KodAciklamasi", "KOD AÇIKLAMASI")}
                                                    >
                                                        <IconFileDescription size={15} />
                                                    </span>
                                                </div>
                                            </div>
                                        </td>
                                        <td style={{ padding: '12px 16px', color: '#334155', fontSize: '13px', lineHeight: '1.45', whiteSpace: 'normal', wordBreak: 'break-word', minWidth: '280px' }}>
                                            <span style={{ fontWeight: 400 }}>
                                                {prd.ParsedDetails?.TamAciklama || formatKatalogKodu(prd.KatalogKodu)}
                                            </span>
                                        </td>
                                        <td style={{ padding: '10px 16px', textAlign: 'center' }}>
                                            {prd.SchemaFile && (
                                                <button
                                                    style={actionBtn('#fff7ed', '#ea580c')}
                                                    onClick={(e) => { e.stopPropagation(); handleShowPopUpSchema(prd); }}
                                                >
                                                    <img width={32} src={schemaIcon} alt="Schema" />
                                                </button>
                                            )}
                                        </td>
                                        <td style={{ padding: '10px 16px', textAlign: 'center' }}>
                                            {prd.PdfFile && (
                                                <button
                                                    style={actionBtn('#fef2f2', '#dc2626')}
                                                    onClick={(e) => { e.stopPropagation(); downloadPdfFile(prd, index); }}
                                                    disabled={downloadingId !== null}
                                                >
                                                    {downloadingId === pdfKey ? (
                                                        <div className="spinner-border spinner-border-sm text-danger" role="status" style={{ width: 20, height: 20 }} />
                                                    ) : (
                                                        <img width={32} src={pdfIcon} alt="PDF" style={{ opacity: downloadingId !== null && downloadingId !== pdfKey ? 0.4 : 1 }} />
                                                    )}
                                                </button>
                                            )}
                                        </td>
                                        <td style={{ padding: '10px 16px', textAlign: 'center' }}>
                                            {prd.StepFile && (
                                                <button
                                                    style={actionBtn('#eff6ff', '#2563a8')}
                                                    onClick={(e) => { e.stopPropagation(); downloadStepFile(prd, index); }}
                                                    disabled={downloadingId !== null}
                                                >
                                                    {downloadingId === stepKey ? (
                                                        <div className="spinner-border spinner-border-sm text-primary" role="status" style={{ width: 20, height: 20 }} />
                                                    ) : (
                                                        <img width={32} src={stepIcon} alt="STEP" style={{ opacity: downloadingId !== null && downloadingId !== stepKey ? 0.4 : 1 }} />
                                                    )}
                                                </button>
                                            )}
                                        </td>
                                        <td style={{ padding: '10px 16px', textAlign: 'center' }}>
                                            {prd.ModelFile && (
                                                <button
                                                    style={actionBtn('#f0fdf4', '#16a34a')}
                                                    onClick={(e) => { e.stopPropagation(); handleShowPopUp3d(prd.ModelFile, prd.OrjinalKodu); }}
                                                >
                                                    <img width={32} src={ucdIcon} alt="3D" />
                                                </button>
                                            )}
                                        </td>
                                        <td style={{ padding: '10px 16px', textAlign: 'center' }}>
                                            <button
                                                className="btn btn-sm btn-outline-primary fw-bold"
                                                onClick={(e) => { e.stopPropagation(); navigate(`/productdetails/${prd.ID || prd.Id}`, {
                                                    state: {
                                                        groupCode,
                                                        rootGroupCode,
                                                        aranacakMetin,
                                                        model3bCheck,
                                                        detailList
                                                    }
                                                }); }}
                                                style={{ borderRadius: '6px', padding: '6px 12px', fontSize: '12px' }}
                                            >
                                                {t('butonDetay', 'Detay')}
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* ── Schema Önizleme Modal ── */}
            <KamaModal
                show={showSchemaModal}
                onHide={() => setShowSchemaModal(false)}
                title={<>{t('tabloBaslikPdfOnizleme')}{orjinalKodu && <span style={{ fontFamily: 'monospace', opacity: 0.85, fontWeight: 500, fontSize: '15px' }}> — {orjinalKodu}</span>}</>}
                icon={<IconFileDescription size={20} />}
                headerGradient="linear-gradient(135deg, #1e2a38 0%, #ea580c 100%)"
                size="xl"
                footer={<button className="btn btn-primary fw-bold px-5" onClick={() => setShowSchemaModal(false)}>{t('butonKapat')}</button>}
            >
                <div style={{ padding: '28px', textAlign: 'center', background: '#f8fafc' }}>
                    <img src={imgSchema} alt="Schema Preview" style={{ maxWidth: '100%', borderRadius: '8px' }} />
                </div>
            </KamaModal>

            {/* ── Ürün Görseli Modal ── */}
            <KamaModal
                show={showImgModal}
                onHide={() => setShowImgModal(false)}
                title={<>{t('tabloBaslikResim')}{orjinalKodu && <span style={{ fontFamily: 'monospace', opacity: 0.85, fontWeight: 500, fontSize: '15px' }}> — {orjinalKodu}</span>}</>}
                icon={<IconPhoto size={20} />}
                size="lg"
                footer={<button className="btn btn-primary fw-bold px-5" onClick={() => setShowImgModal(false)}>{t('butonKapat')}</button>}
            >
                <div style={{ padding: '28px', textAlign: 'center', background: '#f8fafc' }}>
                    <img src={imgProduct} alt="Product" style={{ maxWidth: '100%', borderRadius: '8px' }} />
                </div>
            </KamaModal>

            {/* ── 3D Görünüm Modal ── */}
            <KamaModal
                show={show3dModal}
                onHide={() => setShow3dModal(false)}
                title={<>{t('menu3dView')}{orjinalKodu && <span style={{ fontFamily: 'monospace', opacity: 0.85, fontWeight: 500, fontSize: '15px' }}> — {orjinalKodu}</span>}</>}
                icon={<Icon3dCubeSphere size={20} />}
                headerGradient="linear-gradient(135deg, #1e2a38 0%, #0e7c5a 100%)"
                size="fullscreen"
                footer={<button className="btn btn-outline-secondary fw-bold px-5" onClick={() => setShow3dModal(false)}>{t('butonKapat')}</button>}
                footerStyle={{ background: '#1e2a38', borderTop: '1px solid rgba(255,255,255,0.1)' }}
            >
                <IFrameWindow productUrl={productUrl} />
            </KamaModal>

            {/* ── Ürün Parametre Açıklaması Modal ── */}
            <KamaModal
                show={showSpecModal}
                onClose={() => setShowSpecModal(false)}
                title={t("KamaRuletKodAciklamasi", "Kama Rulet Kod Açıklaması")}
                icon={<IconLayoutGrid size={20} />}
                headerGradient="linear-gradient(135deg, #1e2a38 0%, #2563eb 100%)"
                size="lg"
            >
                <div className="px-2 py-1">
                    {/* Combined Summary Card (Ürün Kodu & Ürün Açıklaması) */}
                    {parsedSpecData && (
                        <div className="card border-0 rounded-4 shadow-sm mb-4" style={{ backgroundColor: "#f8fafc", border: "1px solid #e2e8f0" }}>
                            <div className="card-body p-4">
                                <div className="row g-4 align-items-start">
                                    {/* Sol Kolon: Ürün Kodu */}
                                    <div className="col-12 col-md-5 d-flex flex-column align-items-start pe-md-4" style={{ borderRight: "1px solid #e2e8f0" }}>
                                        <div className="d-flex align-items-center gap-1.5 mb-2" style={{ color: '#94a3b8', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px' }}>
                                            <IconTag size={14} />
                                            {t("KatalogKoduBaslik", "KATALOG KODU")}
                                        </div>
                                        <span className="badge" style={{ backgroundColor: "#1e293b", color: "#ffffff", fontSize: "18px", fontWeight: 700, padding: "7px 14px", borderRadius: "8px", letterSpacing: "0.2px" }}>
                                            {formatKatalogKodu(parsedSpecData.KatalogKodu)}
                                        </span>
                                    </div>

                                    {/* Sağ Kolon: Ürün Açıklaması */}
                                    <div className="col-12 col-md-7 ps-md-4">
                                        <div className="d-flex align-items-center gap-1.5 mb-2" style={{ color: '#94a3b8', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px' }}>
                                            <IconFileDescription size={14} />
                                            {t("TamUrunAciklamasi", "Ürün Açıklaması")}
                                        </div>
                                        <p className="m-0 text-slate-700 fw-medium" style={{ color: "#334155", fontSize: "14px", lineHeight: "1.6" }}>
                                            {parsedSpecData.TamAciklama || t("DetayBulunamadi", "Açıklama bulunamadı.")}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Özellikler Tablosu */}
                    {(() => {
                        const details = parsedSpecData?.Detaylar;
                        const components = parsedSpecData?.Bilesenler;
                        const rows = details ? [
                            { label: t("DetayMarka", "Marka"), val: details.Marka, code: components?.Marka },
                            { label: t("DetayOnKod", "Ön Kod"), val: details.OnKod, code: components?.OnKod },
                            { label: t("DetaySeri", "Seri"), val: details.Seri, code: components?.Seri },
                            { label: t("DetayBaglanti", "Bağlantı Tipi"), val: details.Baglanti, code: components?.Baglanti },
                            { label: t("DetayGovde", "Gövde Cinsi"), val: details.Govde, code: components?.Govde },
                            { label: t("DetaySirtTipi", "Sırt Tipi"), val: details.SirtTipi, code: components?.SirtTipi },
                            { label: t("DetaySirtMalzeme", "Sırt Malzemesi"), val: details.SirtMalzemesi, code: components?.SirtMalzemesi },
                            { label: t("DetayYatak", "Yatak Tipi"), val: details.Yatak, code: components?.Yatak },
                            { label: t("DetayCap", "Tekerlek Çapı"), val: details.Cap, code: components?.Cap },
                            { label: t("DetayGenislik", "Tekerlek Genişliği"), val: details.Genislik, code: components?.Genislik },
                            { label: t("DetayFren", "Fren Tipi"), val: details.Fren, code: components?.Fren },
                            { label: t("DetayYay", "Yay Kodu"), val: details.Yay, code: components?.Yay },
                            { label: t("DetayRenk", "Renk"), val: details.Renk, code: components?.Renk },
                        ].filter(row => row.val) : [];

                        if (rows.length > 0) {
                            return (
                                <div className="table-responsive rounded-4 border overflow-hidden shadow-sm mb-4" style={{ borderColor: "#e2e8f0" }}>
                                    <table className="table table-striped table-hover mb-0 align-middle">
                                        <thead>
                                            <tr style={{ background: "#f8fafc" }}>
                                                <th className="ps-4 py-3 text-secondary font-semibold" style={{ width: "35%", fontSize: "11px", textTransform: "uppercase" }}>{t("Ozellik", "Özellik")}</th>
                                                <th className="py-3 text-secondary font-semibold text-center" style={{ width: "20%", fontSize: "11px", textTransform: "uppercase" }}>{t("KodKarakteri", "Kod Karakteri")}</th>
                                                <th className="pe-4 py-3 text-secondary font-semibold" style={{ fontSize: "11px", textTransform: "uppercase" }}>{t("Aciklama", "Açıklama")}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {rows.map((row, idx) => (
                                                <tr key={idx}>
                                                    <td className="ps-4 py-2.5 fw-semibold text-slate-700" style={{ color: "#475569", fontSize: "13px" }}>
                                                        {row.label}
                                                    </td>
                                                    <td className="py-2.5 text-center">
                                                        <span className="badge px-2 py-0.5 rounded" style={{ backgroundColor: "#f1f5f9", color: "#0f172a", fontSize: "12px", fontWeight: "bold", border: "1px solid #cbd5e1" }}>
                                                            {row.code || "-"}
                                                        </span>
                                                    </td>
                                                    <td className="pe-4 py-2.5 text-slate-900 fw-medium" style={{ color: "#0f172a", fontSize: "13px" }}>
                                                        {row.val}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            );
                        } else if (parsedSpecData) {
                            return (
                                <div className="p-4 text-center text-muted rounded-4 bg-light mb-4">
                                    <IconInfoCircle size={24} className="mb-2" />
                                    <p className="m-0">{t("DetayBulunamadi", "Ürün özellik detay bilgisi bulunamadı.")}</p>
                                </div>
                            );
                        }
                        return null;
                    })()}

                    {/* Additional Information (badges) */}
                    {parsedSpecData?.Detaylar?.Ilave && parsedSpecData.Detaylar.Ilave.length > 0 && (
                        <div className="p-4 rounded-4 bg-light border mb-4" style={{ borderColor: "#e2e8f0" }}>
                            <h6 className="fw-bold text-slate-700 mb-3 d-flex align-items-center gap-2" style={{ color: "#334155" }}>
                                <IconTag size={18} />
                                {t("IlaveBilgiler", "İlave Bilgiler ve Ek Özellikler")}
                            </h6>
                            <div className="d-flex flex-wrap gap-2">
                                {parsedSpecData.Detaylar.Ilave.map((item, idx) => (
                                    <span key={idx} className="badge bg-primary text-white rounded-pill px-3 py-2 fw-semibold" style={{ fontSize: "12px", letterSpacing: "0.2px" }}>
                                        {item}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Modal Footer Row */}
                    <div className="d-flex justify-content-end align-items-center pt-3 pb-2 mt-2">
                        <button
                            type="button"
                            className="btn btn-secondary px-4 py-2 fw-semibold rounded-3 shadow-sm"
                            onClick={() => setShowSpecModal(false)}
                        >
                            {t("Kapat", "Kapat")}
                        </button>
                    </div>
                </div>
            </KamaModal>
        </div>
    )
}
